import React, { useState } from 'react';
import './Custom.css';
import { FaUserPlus, FaCheckCircle, FaEye } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import { utils, writeFile } from 'xlsx';
import { FiSearch } from 'react-icons/fi'; // Search icon
import { FaFilePdf, FaFileExcel } from 'react-icons/fa'; // Importing icons
import API_URLS from '../../summaryapi/apiUrls';

const Custom = ({ bookingData }) => {
    const columns = [
        "S.No", // Add S.No column
        "Booking Code",
        "Category Name",
        "Service Name",
        "Booking Date & Time",
        "Status",
        "Service Date",
        "Service Time",
        "Service Price",
        "Actions"
    ];

    // State for search query, current page, items per page, and time filter
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [timeFilter, setTimeFilter] = useState('All Time'); // New state for time filter
    const [isHovered, setIsHovered] = useState(false);
    const itemsPerPage = 8; // Updated to 8 records per page
    const [bookingStatus, setBookingStatus] = useState([]);

    // Function to filter rows based on the search query and time filter
    const filterData = (data) => {
        return data
            .filter((row) => {
                // Filter by search query
                return Object.values(row).some(val =>
                    String(val).toLowerCase().includes(searchQuery.toLowerCase())
                );
            })
            .filter((row) => {
                // Filter by selected time of day (Morning, Afternoon, Evening, All Time)
                const bookingDate = new Date(row.booking_date); // Assuming booking_date is in a valid date format
                const hours = bookingDate.getHours(); // Get hours from the booking date

                if (timeFilter === 'Morning' && hours >= 6 && hours < 12) {
                    return true;
                }
                if (timeFilter === 'Afternoon' && hours >= 12 && hours < 18) {
                    return true;
                }
                if (timeFilter === 'Evening' && hours >= 18 && hours < 24) {
                    return true;
                }
                if (timeFilter === 'All Time') {
                    return true;
                }
                return false;
            });
    };

    const encodedProviderId = sessionStorage.getItem('provider_id');

    function convertTo24HourFormat(time12h) {
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':').map(Number);

        // Convert PM to 24-hour time
        if (modifier === 'PM' && hours < 12) {
            hours += 12;
        }

        // Convert AM to 24-hour time (e.g., 12 AM becomes 00)
        if (modifier === 'AM' && hours === 12) {
            hours = 0;
        }

        // Format hours and minutes to always show two digits
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    // Pagination logic
    const filteredData = filterData(bookingData);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentPageData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const isNextDisabled = currentPage === totalPages || currentPageData.length === 0;

    // Function to export data to Excel
    const exportToExcel = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Bookings');
        writeFile(wb, 'Bookings_Report.xlsx');
    };

    // Function to export data to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.autoTable({ html: '#todayBookingTable' });
        doc.save('Bookings_Report.pdf');
    };

    const getMostRecentBookingDate = (data) => {
        return data.reduce((latest, row) => {
            const bookingDate = new Date(row.booking_date).getTime();
            return bookingDate > latest ? bookingDate : latest;
        }, 0);
    };

    const getRowStyle = (isViewOrder) => {
        // Apply background color if is_view_order is 0
        return isViewOrder === 0 ? { backgroundColor: '#7CD6F5' } : {};
    };

    // In your render method
    const mostRecentDate = getMostRecentBookingDate(currentPageData);

    // Function to handle text color for different statuses
    const getStatusTextStyle = (status) => {
        switch (status) {
            case 'Pending':
                return { color: 'red' };
            case 'Confirmed':
                return { color: 'green' };
            case 'Scheduled':
                return { color: 'orange' };
            case 'Cancelled':
                return { color: 'brown' };
            default:
                return {};
        }
    };

    const handleAssignClick = (booking) => {
        const { booking_id, user_id, service_id, provider_id, status, is_view_order } = booking;
    
        const updateViewOrderDetails = async () => {
            try {
                const response = await fetch(API_URLS.updateviewOrderDetails.url, {
                    method: API_URLS.updateviewOrderDetails.method,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        booking_id: booking_id,
                        is_view_order: 1,
                    }),
                });
    
                if (response.ok) {
                    const result = await response.json();
                    // Reload the window after successful update
                    window.location.reload();
                } else {
                    console.error('Error updating view order:', response.statusText);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        };
    
        const encodeBase64 = (value) => {
            return btoa(value);
        };
    
        const encodedBookingId = encodeBase64(booking_id);
        const encodedUserId = encodeBase64(user_id);
        const encodedServiceId = encodeBase64(service_id);
        const encodedProviderId = provider_id ? encodeBase64(provider_id) : null;
    
        let url = `/AssignOrders?booking_id=${encodedBookingId}&user_id=${encodedUserId}&service_id=${encodedServiceId}`;
    
        if (status === 'Confirmed' && encodedProviderId) {
            url += `&provider_id=${encodedProviderId}`;
        }
    
        window.open(url, '_blank');
    
        if (is_view_order === 0) {
            updateViewOrderDetails();
        }
    };

    
    return (
        <div className="today-table-container">
            <h3 style={{ color: '#8A2BE2' }}>Today Bookings</h3>
            {/* Table Header with Search */}
            <div className="today-table-header">
                {/* Search Bar */}
                <div className="today-search-container">
                    <input
                        type="text"
                        placeholder="Search Today Bookings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="today-search-input"
                        onMouseEnter={() => setIsHovered(true)}  // Set hover state to true
                        onMouseLeave={() => setIsHovered(false)}  // Set hover state to false
                        style={{
                            padding: '8px',
                            borderRadius: '4px',
                            border: isHovered ? '3px solid lightblue' : '1px solid lightblue', // Apply border on hover
                            minWidth: '200px',
                            marginRight: '8px', // Space between input and icon
                            outline: 'none', // Remove default outline on focus
                            transition: 'border 0.3s ease', // Smooth border transition
                        }}
                    />
                    <FiSearch style={{ fontSize: '20px', color: '#888' }} /> {/* Search Icon */}
                </div>

                {/* Buttons section (Export buttons + New buttons) */}
                <div className="today-buttons-container">
                    {/* Export Buttons */}
                    <div className="today-export-buttons">
    <button
        style={{
            width: '150px',
            backgroundColor: filteredData.length === 0 ? '#ccc' : 'red', // Disable if no data
            height: '40px',
            color: 'white',
            textAlign: 'center',
            border: 'none',
            borderRadius: '8px',
            cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', // Disable if no data
        }}
        onClick={exportToPDF}
        disabled={filteredData.length === 0} // Disable if no data
    >
        <span style={{ fontSize: '15px' }}> Download </span> <FaFilePdf style={{ fontSize: '15px' }} />
    </button>
    <button
        style={{
            width: '150px',
            backgroundColor: filteredData.length === 0 ? '#ccc' : 'green', // Disable if no data
            height: '40px',
            color: 'white',
            textAlign: 'center',
            border: 'none',
            borderRadius: '8px',
            cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', // Disable if no data
        }}
        onClick={exportToExcel}
        disabled={filteredData.length === 0} // Disable if no data
    >
        Download <FaFileExcel style={{ fontSize: '15px' }} />
    </button>
</div>
                </div>
            </div>

            {/* Scrollable Table Wrapper */}
            <div className="today-custom-table-wrapper">
                <table id="todayBookingTable" className="today-custom-table">
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th key={index}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentPageData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                   <td style={getRowStyle(row.is_view_order)}>
                                        {(currentPage - 1) * itemsPerPage + rowIndex + 1}
                                    </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    <strong>{row.booking_code || 'N/A'}</strong>
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    {row.category_name || 'N/A'}
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    {row.service_name || 'N/A'}
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    <div style={{ fontSize: '14px' }}>
                                        {new Date(row.booking_date).toLocaleDateString()}
                                    </div>
                                    <div style={{ marginTop: '4px' }}>
                                        <strong>{new Date(row.booking_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
                                    </div>
                                </td>
                                <td style={{ ...getRowStyle(row.is_view_order), ...getStatusTextStyle(row.status) }}>
                                    {row.status || 'N/A'}
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    <strong>{row.service_date || 'N/A'}</strong>
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    <strong>{row.service_time || 'N/A'}</strong>
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
                                    {row.service_price ? `₹${Number(row.service_price).toFixed(2)}` : 'N/A'}
                                </td>
                                <td style={getRowStyle(row.is_view_order)}>
    {row.status.toLowerCase().trim() === 'pending' ? (
        <button className="today-assign-button" onClick={() => handleAssignClick(row)}>
            <FaUserPlus /> Assign
        </button>
    ) : (
        <button className="today-assigned-button" onClick={() => handleAssignClick(row)}>
            <FaCheckCircle /> Assigned
        </button>
    )}
</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="today-table-footer">
                {/* Total Records - Left */}
                <div className="today-total-records-container">
                    <strong><span className="today-total-records">Total Records: {filteredData.length}</span></strong>
                </div>

                {/* Pagination Controls - Center */}
                <div className="today-pagination-container">
                    <div className="today-pagination-controls">
                        <button
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="today-pagination-button"
                            style={{
                                backgroundColor: currentPage === 1 ? '#ccc' : '#ff4d4d', // Red when disabled
                                color: currentPage === 1 ? '#666' : '#fff', // Text color based on state
                            }}
                        >
                            Previous
                        </button>
                        <span style={{ margin: '10px' }}>
                            page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={isNextDisabled} // Disable the button if no data is available for the next page
                            className="today-pagination-button"
                            style={{
                                margin: '10px',
                                backgroundColor: isNextDisabled ? '#ccc' : '#4caf50', // Green when active, grey when disabled
                                color: isNextDisabled ? '#666' : '#fff', // Text color based on state
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Custom;